import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Theme, Typography } from '@material-ui/core'
import ErrorIcon from '@material-ui/icons/Error'
import { RelatedContentCard } from '@sial/aem-cms-components/dist/RelatedContentCard'
import {
  Brand,
  BrowserMetadata,
  CatalogImage,
  CatalogType,
  Maybe,
  Product,
  ProductInput,
} from '@src/types/graphql-types'
import { useMultipleProductDetailPartialProductsQuery } from '@src/queries/MultipleProductDetailsQueryFullProduct.generated'
import { FormattedMessage } from 'react-intl'

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {},
  title: {
    display: 'block',
    fontSize: theme.typography.pxToRem(20),
    lineHeight: '1.2em',
    fontWeight: 900,
    margin: theme.spacing(6, 0, 4),
    textTransform: 'uppercase',
    '.aem-Grid--default--3 &, .aem-Grid--default--4 &': {
      fontSize: theme.typography.pxToRem(18),
      textTransform: 'initial',
    },
  },
  list: {
    width: '100%',
  },
  error: {
    padding: theme.spacing(6, 5, 3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  noProducts: {
    textAlign: 'center',
    padding: theme.spacing(2, 2),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
}))

interface Props {
  title: string
  products: ProductInput[]
  totalProductsDisplay: number
}
type RelatedProductDataProps = Maybe<
  { __typename?: 'Product' } & Pick<
    Product,
    'id' | 'name' | 'description' | 'productNumber'
  > & {
      brand: { __typename?: 'Brand' } & Pick<Brand, 'key'>
      browserMetadata?: Maybe<
        { __typename?: 'BrowserMetadata' } & Pick<
          BrowserMetadata,
          'description'
        >
      >
      images: { __typename?: 'CatalogImage' } & Pick<
        CatalogImage,
        'altText' | 'mediumUrl'
      >[]
    }
>

const getUpdatedInputsBasedOnCatalogType = (productInputs) =>
  productInputs.map((product) => ({
    ...product,
    catalogType: CatalogType.Sial,
  }))

const RelatedProductsRightRail: React.FC<Props> = ({
  title,
  products,
  totalProductsDisplay,
}) => {
  const classes = useStyles()

  const updatedProductInputs = getUpdatedInputsBasedOnCatalogType(products)

  const productInputs: ProductInput[] = updatedProductInputs?.map(
    ({ brandKey, productKey, catalogType }) => ({
      brandKey,
      productKey,
      catalogType,
    })
  )

  const { loading, error, data } = useMultipleProductDetailPartialProductsQuery(
    {
      variables: {
        productInputs,
      },
      errorPolicy: 'ignore',
      ssr: false,
      // client
    }
  )
  if (loading) return <div className={classes.wrapper}></div>
  if (error) {
    console.error(error)
    if (products && products.length > 0) {
      return (
        <div className={classes.error}>
          <ErrorIcon />
          <span>
            <FormattedMessage
              id="SOMETHING_WENT_WRONG_TEXT"
              defaultMessage="Something went wrong, please try again."
            />
          </span>
        </div>
      )
    } else {
      return null
    }
  } else {
    const relatedProductData = data ? data.getMultipleProductDetails : []
    return (
      <div
        aria-labelledby="Related Product Right Rail"
        className={
          'aem-ms-cmp aem-ms-grid-view aem-ms-related-product-right-rail'
        }
      >
        {title && (
          <Typography
            component="strong"
            className={classes.title}
            dangerouslySetInnerHTML={{ __html: title }}
          />
        )}
        <ul>
          {relatedProductData &&
            relatedProductData.map(
              (product: RelatedProductDataProps, index: number) => {
                if (!product) return null
                const productDescription =
                  product.description && product.description != null
                    ? product.description
                    : product?.browserMetadata?.description
                if (index < totalProductsDisplay) {
                  return (
                    <li className={classes.list} key={index}>
                      <RelatedContentCard
                        url={
                          '/product/' +
                          product.brand.key +
                          '/' +
                          product.productNumber
                        }
                        title={product.name}
                        text={productDescription || ''}
                        maxTitleLines={2}
                      />
                    </li>
                  )
                } else {
                  return false
                }
              }
            )}
        </ul>
      </div>
    )
  }
}
export default RelatedProductsRightRail
